import { t } from "../../i18n";
import { Check, LoaderCircle, Save } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { api } from "../../api";
import { SettingsPage } from "../settings/ProfilePage";
import { ModelSearchPicker } from "../../components/composer/ModelSearchPicker";
import { Toggle } from "../../components/Toggle";
import type { RichModel } from "../models/ModelsPage.utils";

type CommandApprovalWorkflow =
  "request_approval" | "model_fallback" | "model_deny" | "accept_all";

type WorkflowSettings = {
  commandApprovalWorkflow: CommandApprovalWorkflow;
  globalCommandApprovalModelId: string | null;
  personalCommandApprovalModelId: string | null;
  effectiveCommandApprovalModelId: string | null;
  commandApprovalModelScope?: "global" | "personal";
};

const workflowLabels: Record<CommandApprovalWorkflow, string> = {
  request_approval: "Request approval",
  model_fallback: "Use model fallback to approval",
  model_deny: "Use model and deny",
  accept_all: "Accept all",
};

export function WorkflowSettingsPage({
  canManageGlobal = false,
}: {
  canManageGlobal?: boolean;
}) {
  const [settings, setSettings] = useState<WorkflowSettings | null>(null);
  const [models, setModels] = useState<RichModel[]>([]);
  const [modelScope, setModelScope] = useState<"global" | "personal">(
    "personal",
  );
  const [busy, setBusy] = useState(true);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const settingsPath = canManageGlobal
      ? "/admin/workflow-settings"
      : "/me/workflow-settings";
    void Promise.all([
      api<WorkflowSettings>(settingsPath),
      api<RichModel[]>("/models/settings"),
    ])
      .then(([nextSettings, nextModels]) => {
        setSettings(nextSettings);
        setModelScope(nextSettings.commandApprovalModelScope ?? "personal");
        setModels(nextModels);
      })
      .catch((reason) =>
        setError(
          reason instanceof Error
            ? reason.message
            : t("Workflow settings could not be loaded"),
        ),
      )
      .finally(() => setBusy(false));
  }, [canManageGlobal]);

  const availableModels = useMemo(
    () =>
      models.filter(
        (model) =>
          model.enabled &&
          model.userEnabled !== false &&
          model.providerEnabled !== false &&
          (modelScope === "personal" || model.scope !== "personal"),
      ),
    [modelScope, models],
  );
  const selectedModelId =
    modelScope === "global"
      ? (settings?.globalCommandApprovalModelId ?? "")
      : (settings?.personalCommandApprovalModelId ?? "");
  const effectiveModelId =
    modelScope === "personal" && selectedModelId
      ? selectedModelId
      : (settings?.effectiveCommandApprovalModelId ?? "");

  const save = async () => {
    if (!settings) return;
    setBusy(true);
    setSaved(false);
    setError("");
    try {
      if (canManageGlobal) {
        const result = await api<
          WorkflowSettings & { commandApprovalModelId?: string | null }
        >("/admin/workflow-settings", {
          method: "PATCH",
          body: JSON.stringify({
            commandApprovalWorkflow: settings.commandApprovalWorkflow,
            commandApprovalModelScope: modelScope,
            commandApprovalModelId: selectedModelId || null,
          }),
        });
        setSettings((current) => ({
          ...current!,
          ...result,
          ...(modelScope === "global"
            ? {
                globalCommandApprovalModelId:
                  result.commandApprovalModelId ?? null,
              }
            : { personalCommandApprovalModelId: selectedModelId || null }),
          effectiveCommandApprovalModelId:
            modelScope === "global"
              ? (result.commandApprovalModelId ??
                current!.personalCommandApprovalModelId)
              : selectedModelId || current!.globalCommandApprovalModelId,
        }));
      } else {
        const result = await api<{
          personalCommandApprovalModelId: string | null;
        }>("/me/workflow-settings", {
          method: "PATCH",
          body: JSON.stringify({
            commandApprovalModelId: selectedModelId || null,
          }),
        });
        setSettings((current) => ({
          ...current!,
          personalCommandApprovalModelId: result.personalCommandApprovalModelId,
          effectiveCommandApprovalModelId:
            result.personalCommandApprovalModelId ??
            current!.globalCommandApprovalModelId,
        }));
      }
      setSaved(true);
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : t("Workflow settings could not be saved"),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <SettingsPage
      title={t("Workflow")}
      description={t(
        "Configure how agent actions that need command approval are handled.",
      )}
    >
      <section className="panel max-w-2xl p-5 sm:p-6">
        {settings && (
          <>
            <label>
              <span className="label">{t("Command approval behavior")}</span>
              <select
                className="field"
                disabled={busy || !canManageGlobal}
                value={settings.commandApprovalWorkflow}
                onChange={(event) => {
                  setSettings((current) => ({
                    ...current!,
                    commandApprovalWorkflow: event.target
                      .value as CommandApprovalWorkflow,
                  }));
                  setSaved(false);
                }}
              >
                {Object.entries(workflowLabels).map(([value, label]) => (
                  <option key={value} value={value}>
                    {t(label)}
                  </option>
                ))}
              </select>
              {!canManageGlobal && (
                <span className="mt-2 block text-xs text-stone-400">
                  {t(
                    "The site administrator controls the global approval behavior.",
                  )}
                </span>
              )}
            </label>
            {canManageGlobal && (
              <div className="mt-5">
                <span className="label">{t("Approval model location")}</span>
                <label className="mt-2 flex items-center justify-between gap-4 text-sm">
                  <span>
                    {modelScope === "global"
                      ? t("Set global model")
                      : t("Set my personal model")}
                  </span>
                  <Toggle
                    aria-label={t("Set global model")}
                    checked={modelScope === "global"}
                    disabled={busy}
                    onChange={(event) => {
                      setModelScope(
                        event.target.checked ? "global" : "personal",
                      );
                      setSaved(false);
                    }}
                  />
                </label>
              </div>
            )}
            <div className="mt-5">
              <span className="label">
                {canManageGlobal && modelScope === "global"
                  ? t("Global command approval model")
                  : t("Personal command approval model")}
              </span>
              <ModelSearchPicker
                models={availableModels}
                value={selectedModelId}
                disabled={busy}
                emptyLabel={t("Use the global approval model")}
                onChange={(value) => {
                  const next = value || null;
                  setSettings((current) => ({
                    ...current!,
                    ...(modelScope === "global"
                      ? { globalCommandApprovalModelId: next }
                      : {
                          personalCommandApprovalModelId: next,
                          effectiveCommandApprovalModelId:
                            next ?? current!.globalCommandApprovalModelId,
                        }),
                  }));
                  setSaved(false);
                }}
              />
              <p className="mt-2 text-xs text-stone-400">
                {t(
                  "A personal model overrides the global model for your commands. Workspace deny rules still apply.",
                )}
              </p>
            </div>
            {settings.commandApprovalWorkflow !== "request_approval" &&
              settings.commandApprovalWorkflow !== "accept_all" &&
              !effectiveModelId && (
                <p className="mt-4 text-sm text-amber-700 dark:text-amber-300">
                  {t("Choose an approval model or use Request approval.")}
                </p>
              )}
            {error && (
              <p className="mt-4 text-sm text-red-600 dark:text-red-300">
                {error}
              </p>
            )}
            <div className="mt-5 flex justify-end">
              <button
                className="button-primary"
                disabled={busy}
                onClick={() => void save()}
              >
                {busy ? (
                  <LoaderCircle className="animate-spin" size={16} />
                ) : saved ? (
                  <Check size={16} />
                ) : (
                  <Save size={16} />
                )}{" "}
                {saved ? t("Saved") : t("Save")}
              </button>
            </div>
          </>
        )}
      </section>
    </SettingsPage>
  );
}
